import { ActionButton, Calendar, DayOfWeek, IContextualMenuItem } from "office-ui-fabric-react";
import React, { useState } from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import { getFirstDayOfWeekSetting } from "../../store/selectors/settingsSelectors";
import { DayPickerStrings } from "../common/inputs/DatePickerInput";
import { getDaysBetweenDates } from "../common/timeline/utils";
import {
    TimeReportingPeriod,
    getEarliestOpenPeriodDate,
    getLatestOpenPeriodDate,
    isDateInClosedPeriod
} from "../../store/TimeReportingPeriodsStore";

type OwnProps = {
    defaultDate: Date;
    minDate: Date;
    maxDate: Date;
    restrictedDates: Date[];
    isDateAvailable: (date: Date) => boolean;
    clone: (date: Date) => void;
};

type StoreProps = {
    firstDayOfWeek: DayOfWeek;
};

type Props = OwnProps & StoreProps;

function TimeTrackingCloneToDateMenu(props: Props) {
    const { defaultDate, minDate, maxDate, restrictedDates, isDateAvailable, clone, firstDayOfWeek } = props;

    const [date, setDate] = useState(defaultDate);

    return (
        <div className="time-tracking-menu">
            <Calendar
                value={date}
                onSelectDate={_ => setDate(_)}
                minDate={minDate}
                maxDate={maxDate}
                restrictedDates={restrictedDates}
                strings={DayPickerStrings}
                firstDayOfWeek={firstDayOfWeek}
                showGoToToday
            />

            <div className="ms-ContextualMenu-link">
                <ActionButton
                    className="ms-ContextualMenu-linkContent full-width"
                    text="Clone to the selected date"
                    iconProps={{ iconName: "Clock" }}
                    disabled={!isDateAvailable(date)}
                    onClick={() => clone(date)}
                />
            </div>
        </div>
    );
}

const ConnectedTimeTrackingCloneToDateMenu = connect((state: ApplicationState): StoreProps => ({
    firstDayOfWeek: getFirstDayOfWeekSetting(state)
}))(TimeTrackingCloneToDateMenu);

export function buildCloneToDateMenuItem(
    entryDate: Date,
    periods: TimeReportingPeriod[],
    clone: (date: Date) => void
): IContextualMenuItem | undefined {
    const minDate = getEarliestOpenPeriodDate(periods);
    const maxDate = getLatestOpenPeriodDate(periods);

    if (!minDate || !maxDate) {
        return undefined;
    }

    const entryTime = entryDate.getBeginOfDay().getTime();
    const isDateAvailable = (date: Date) =>
        date.getBeginOfDay().getTime() !== entryTime && !isDateInClosedPeriod(date, periods);

    const days = getDaysBetweenDates(minDate, maxDate);
    const restrictedDates = days.filter(_ => !isDateAvailable(_));
    const defaultDate = days.find(_ => _.getTime() > entryTime && isDateAvailable(_)) ?? days.find(isDateAvailable);

    if (!defaultDate) {
        return undefined;
    }

    return {
        key: "specificDate",
        text: "Specific date",
        iconProps: {
            iconName: "Calendar"
        },
        subMenuProps: {
            items: [
                {
                    key: "timetracking-clone-to-date",
                    onRender: (item: any, dismiss: (ev?: any, dismissAll?: boolean) => void) => (
                        <ConnectedTimeTrackingCloneToDateMenu
                            defaultDate={defaultDate}
                            minDate={minDate}
                            maxDate={maxDate}
                            restrictedDates={restrictedDates}
                            isDateAvailable={isDateAvailable}
                            clone={date => {
                                clone(date);
                                dismiss(null, true);
                            }}
                        />
                    )
                }
            ]
        }
    };
}
